import type { OfferRewardType } from "../../api/dto/offers.dto.js";
import type { ImgID } from "../../image_loader/img_id.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { BuyBoosterType, getBoosterTextVariableParams, getDefaultBoosterEffectValue } from "../dialog/buy_booster_types.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";
import { CumulativeBoosterID } from "../../cumulative_boosters/cumulative_booster_id.js";
import { AnchoredTooltip } from "./anchored_tooltip.js";

/** Reward names used by adventure boxes, daily tasks and level results. */
export type LegacyRewardType = "coin" | "heart" | "infinite_hearts" | "no_ads" | "fireworks" | "multiballs" | "big_balls" | "infinite_balls" | "mines";

/**
 * Short name tooltips for rewards ("Золото", "10 куль", "Без реклами"). Use
 * attach() only where a tap does nothing else; where a tap is taken (claiming
 * a task), open show() from a long press instead.
 */
export class RewardTooltip
{
  private static readonly OWNER = "reward";

  private static readonly BOOSTER_BY_REWARD: Partial<Record<OfferRewardType, BuyBoosterType>> = {
    big_balls: BuyBoosterType.BigBall,
    multi_balls: BuyBoosterType.FiveBalls,
    fireworks: BuyBoosterType.Firework,
    infinite_balls: BuyBoosterType.InfiniteShots,
  };

  private static readonly BOOSTER_TITLE_KEYS: Record<BuyBoosterType, string> = {
    [BuyBoosterType.Firework]: "BOOSTER.FIREWORK",
    [BuyBoosterType.FiveBalls]: "BOOSTER.FIVE_BALLS",
    [BuyBoosterType.BigBall]: "BOOSTER.BIG_BALL",
    [BuyBoosterType.InfiniteShots]: "BOOSTER.INFINITE_SHOTS",
  };

  /** infinite_hearts and no_ads carry minutes there, so they map to the timed offer types. */
  private static readonly OFFER_TYPE_BY_LEGACY: Record<Exclude<LegacyRewardType, "mines">, OfferRewardType> = {
    coin: "coins",
    heart: "hearts",
    infinite_hearts: "infinite_hearts_minutes",
    no_ads: "no_ads_minutes",
    fireworks: "fireworks",
    multiballs: "multi_balls",
    big_balls: "big_balls",
    infinite_balls: "infinite_balls",
  };

  /** Short localized name of a reward. */
  public static nameOf( type: OfferRewardType ): string
  {
    const booster_type = RewardTooltip.BOOSTER_BY_REWARD[type];
    if ( booster_type !== undefined )
    {
      const params = getBoosterTextVariableParams( booster_type, RewardTooltip.boosterEffectValue( booster_type ) );
      return LocalizationManager.tWithParams( RewardTooltip.BOOSTER_TITLE_KEYS[booster_type], params );
    }

    switch ( type )
    {
      case "coins": return LocalizationManager.t( "SHOP.TAB.GOLD" );
      case "hearts": return LocalizationManager.t( "LIVES" );
      case "infinite_hearts_minutes": return LocalizationManager.t( "INFINITE.HEARTS" );
      case "level_shots": return LocalizationManager.t( "LEVEL_SHOTS" );
      case "mines": return LocalizationManager.t( "PASSIVE.BOOSTER.MINES" );
      default: return LocalizationManager.t( "NO_ADS" );
    }
  }

  /** Daily task stars, which are progress points rather than an offer reward. */
  public static starsName(): string
  {
    return LocalizationManager.t( "STARS" );
  }

  /**
   * Makes `anchor` (a reward icon or card) show the reward's name on tap.
   * Labels drawn over the icon swallow taps, so pass them as `extra_targets`
   * to open the same tooltip.
   */
  public static attach( anchor: UIElement, type: OfferRewardType, extra_targets: readonly UIElement[] = [] ): void
  {
    RewardTooltip.attachName( anchor, () => RewardTooltip.nameOf( type ), extra_targets );
  }

  public static attachLegacy( anchor: UIElement, type: LegacyRewardType ): void
  {
    RewardTooltip.attachName( anchor, () => RewardTooltip.nameOfLegacy( type ) );
  }

  public static nameOfLegacy( type: LegacyRewardType ): string
  {
    if ( type === "mines" )
      return LocalizationManager.t( "PASSIVE.BOOSTER.MINES" );
    return RewardTooltip.nameOf( RewardTooltip.OFFER_TYPE_BY_LEGACY[type] );
  }

  public static attachWithNote( anchor: UIElement, get_content: () => { name: string; note: string; note_icon: ImgID }, extra_targets: readonly UIElement[] = [] ): void
  {
    RewardTooltip.onTap( anchor, extra_targets, () =>
    {
      const content = get_content();
      AnchoredTooltip.show( anchor, {
        text: content.name,
        note: content.note,
        note_icon: content.note_icon,
        anchor_touch_closes: true,
      }, RewardTooltip.OWNER );
    } );
  }

  public static attachName( anchor: UIElement, get_name: () => string, extra_targets: readonly UIElement[] = [] ): void
  {
    RewardTooltip.onTap( anchor, extra_targets, () => RewardTooltip.show( anchor, get_name() ) );
  }

  private static onTap( anchor: UIElement, extra_targets: readonly UIElement[], show: () => void ): void
  {
    anchor.setOnClick( show );
    for ( const target of extra_targets )
      target.setOnClick( show );
  }

  /** Opens the name tooltip directly (also from a long press); a touch on the same reward closes it. */
  public static show( anchor: UIElement, name: string ): void
  {
    AnchoredTooltip.show( anchor, { text: name, compact: true, anchor_touch_closes: true }, RewardTooltip.OWNER );
  }

  /** The configured value the play / try-again dialogs put into the booster name ("10 куль"). */
  private static boosterEffectValue( type: BuyBoosterType ): number | null
  {
    switch ( type )
    {
      case BuyBoosterType.FiveBalls:
        return CumulativeBoosterService.getBoosterProperties( CumulativeBoosterID.Multiball )?.ball_count
          ?? getDefaultBoosterEffectValue( type );
      case BuyBoosterType.InfiniteShots:
        return CumulativeBoosterService.getBoosterProperties( CumulativeBoosterID.InfiniteBalls )?.duration_seconds
          ?? getDefaultBoosterEffectValue( type );
      default:
        return getDefaultBoosterEffectValue( type );
    }
  }
}
